/**
 * PersonalizedRecommendations — "Your TruBeauté Routine" surface.
 *
 * REPLACES the earlier standalone `ShadeMatchesCard` (Jul 8 2026 build).
 * The founder's demo feedback: shade-matching felt like a separate
 * technical feature. This surface folds the same `/api/shade-match/
 * analyze` output into a Trouvé-voice, product-first experience that
 * mirrors what a smart beauty advisor would hand you — foundation
 * hero → concealer strategy (as steps) → lip tabs → save-routine CTA.
 *
 * Contract preserved: same `useShadeMatch(...)` hook, same request
 * payload, same backend response schema. Zero backend changes.
 *
 * Demo-safety rules honored:
 *   - Missing product image → polished swatch fallback (depth+undertone
 *     tinted gradient — see `swatchStyle()`).
 *   - Missing `product_url` → hide any Buy CTA (we don't inject affiliate
 *     links until the catalog supports them).
 *   - Empty section → hidden.
 *   - Numeric scores + LAB/ΔE math → tucked behind "Debug details".
 */
import React, { useMemo, useState } from "react";
import { ChevronDown, Sparkles, Camera, RotateCcw, Bookmark } from "lucide-react";
import useShadeMatch from "../lib/useShadeMatch";

// ─────────────────────────────────────────────────────────────
// Visual tokens (aligned with the routine page palette)
// ─────────────────────────────────────────────────────────────
const BADGE_STYLES = {
  "Shade Twin":    "bg-[#9B6A6C] text-white",
  "Strong Match":  "bg-[#B48A8C] text-white",
  "Good Match":    "bg-[#EADDD7] text-[#5D4050]",
  "Worth Testing": "bg-[#F6EFEA] text-[#8A7D82] border border-[#EADDD7]",
};

// Build a soft radial-gradient swatch approximating the shade's real
// color. We don't have per-shade LAB on the frontend match payload
// (only depth_score + undertones + finish), so we hash those into a
// tinted radial gradient. Feels premium and never falls back to a
// broken image icon during a live demo.
const swatchStyle = (match) => {
  const depth = Math.max(0, Math.min(100, Number(match?.depth_score) || 40));
  // Lightness: light shades → high L, dark shades → low L
  const L = Math.round(94 - depth * 0.62); // 94 → 32 approx
  const undertone = (match?.undertones || [])[0] || "neutral";
  const hueMap = {
    warm:    30,
    cool:    340,
    neutral: 25,
    olive:   38,
    peach:   18,
    golden:  35,
    red:     10,
  };
  // Lip shades: shift toward the pink/rose end for visual truth
  const isLip = match?.product_type === "lip";
  const hue = isLip ? 350 - (depth * 0.4) : (hueMap[undertone] ?? 25);
  const sat = isLip ? 55 : 22;
  const center = `hsl(${hue}, ${sat}%, ${Math.max(38, L)}%)`;
  const edge   = `hsl(${hue}, ${Math.max(14, sat - 8)}%, ${Math.max(24, L - 10)}%)`;
  const rim    = `hsl(${hue}, ${Math.max(10, sat - 12)}%, ${Math.max(18, L - 18)}%)`;
  return {
    background: `radial-gradient(circle at 30% 28%, ${center} 0%, ${edge} 62%, ${rim} 100%)`,
  };
};

// ─────────────────────────────────────────────────────────────
// Trouvé-voice copy — first person, warm, purpose-specific.
// ─────────────────────────────────────────────────────────────
const trouveFoundationCopy = (match) => {
  if (!match) return "";
  const badge = match.badge;
  if (badge === "Shade Twin") {
    return "I’d start with this foundation. The depth and undertone read like a mirror of your skin.";
  }
  if (badge === "Strong Match") {
    return "I’d start with this foundation. The shade is close to your measured skin tone and matches your undertone.";
  }
  if (badge === "Good Match") {
    return "I’d start here. Very close on depth — swatch in-store to lock it in.";
  }
  return "I’d try this one first. Catalog is limited today, so treat it as a launchpad, not a verdict.";
};

const trouveConcealerCopy = (purpose) => {
  const copy = {
    beard_shadow: {
      title: "Correct beard shadow",
      instruction: "Use this first to soften beard shadow before your regular concealer.",
    },
    redness: {
      title: "Neutralize redness",
      instruction: "Press this on pink/red patches before your concealer — it cancels the color out.",
    },
    hyperpigmentation: {
      title: "Warm up dark spots",
      instruction: "A peach-warm layer under your concealer lifts stubborn spots without going ashy.",
    },
    spot_conceal: {
      title: "Spot conceal",
      instruction: "Tap this onto blemishes only. Match the depth so it disappears into your foundation.",
    },
    brighten_under_eye: {
      title: "Brighten under eyes",
      instruction: "This shade is slightly lighter to brighten without looking stark.",
    },
  };
  return copy[purpose] || copy.spot_conceal;
};

const trouveLipCopy = (vibe) => {
  const copy = {
    everyday: "This works as an everyday lip because it complements your undertone and contrast level.",
    nude:     "A quiet nude that reads like your lips, only richer — great for a soft day.",
    statement:"When you want to be seen. Saturated enough to hold the room, tuned to your undertone.",
  };
  return copy[vibe] || copy.everyday;
};

const confidenceCopy = (conf) => {
  const pct = Math.round((Number(conf) || 0) * 100);
  if (pct >= 85) return {
    tone: "high",
    line: "Great, I have enough to build your routine.",
    hint: null,
    action: null,
  };
  if (pct >= 65) return {
    tone: "medium",
    line: "This works, but one more selfie would make your match even better.",
    hint: null,
    action: { primary: "Add one more selfie", secondary: "Continue anyway" },
  };
  return {
    tone: "low",
    line: "The lighting is making this tricky. Try one more near a window.",
    hint: null,
    action: { primary: "Retake selfie", secondary: null },
  };
};

// ─────────────────────────────────────────────────────────────
// Tiny presentational atoms
// ─────────────────────────────────────────────────────────────
const MatchBadge = ({ badge }) => (
  <span
    className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-medium uppercase tracking-wider ${
      BADGE_STYLES[badge] || BADGE_STYLES["Worth Testing"]
    }`}
    data-testid={`match-badge-${(badge || "").toLowerCase().replace(/\s+/g, "-")}`}
  >
    {badge || "Worth Testing"}
  </span>
);

const Swatch = ({ match, size = "lg" }) => {
  const sizes = {
    xl: "h-32 w-32 sm:h-36 sm:w-36",
    lg: "h-24 w-24",
    md: "h-16 w-16",
    sm: "h-12 w-12",
  };
  const hasImage = !!match?.image_url;
  return (
    <div
      className={`${sizes[size]} rounded-2xl shadow-inner ring-1 ring-black/5 shrink-0 overflow-hidden`}
      style={hasImage ? undefined : swatchStyle(match)}
      data-testid={`swatch-${match?.product_type}-${(match?.shade_name || "").toLowerCase().replace(/\s+/g, "-")}`}
    >
      {hasImage && (
        <img
          src={match.image_url}
          alt={`${match.brand} ${match.shade_name}`}
          className="h-full w-full object-cover"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Foundation Hero — visually dominant, first-person Trouvé copy.
// ─────────────────────────────────────────────────────────────
const FoundationHero = ({ match, onSave }) => {
  const [whyOpen, setWhyOpen] = useState(false);
  if (!match) return null;
  return (
    <div
      className="rounded-3xl bg-white border border-[#EADDD7] p-6 sm:p-8 shadow-sm"
      data-testid="foundation-hero-card"
    >
      <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 items-start">
        <Swatch match={match} size="xl" />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#9B6A6C] mb-2">
            Foundation
          </p>
          <h3 className="font-serif italic text-2xl sm:text-3xl text-[#2C2123] leading-tight">
            {match.brand}
          </h3>
          <p className="text-base text-[#5C4B51] mt-1">
            {match.product_name}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <span className="text-sm text-[#8A7D82]">
              Shade — <span className="text-[#2C2123] font-medium">{match.shade_name}</span>
            </span>
            <MatchBadge badge={match.badge} />
          </div>
          <p className="mt-4 text-[15px] leading-relaxed text-[#3A2C30]">
            {trouveFoundationCopy(match)}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            {onSave && (
              <button
                type="button"
                onClick={() => onSave(match)}
                className="inline-flex items-center gap-2 rounded-full bg-[#9B6A6C] hover:bg-[#7d5455] text-white px-5 py-2.5 text-sm font-medium transition-colors"
                data-testid="foundation-save-to-routine-btn"
              >
                <Bookmark className="h-4 w-4" /> Save to routine
              </button>
            )}
            <button
              type="button"
              onClick={() => setWhyOpen((v) => !v)}
              className="inline-flex items-center gap-1 rounded-full border border-[#EADDD7] text-[#5D4050] px-4 py-2 text-sm hover:bg-[#FBF6F1] transition-colors"
              data-testid="foundation-why-toggle"
            >
              Why this works
              <ChevronDown className={`h-4 w-4 transition-transform ${whyOpen ? "rotate-180" : ""}`} />
            </button>
          </div>
          {whyOpen && (
            <p
              className="mt-4 text-sm text-[#5C4B51] bg-[#FBF6F1] rounded-xl p-4 leading-relaxed"
              data-testid="foundation-why-body"
            >
              {match.explanation}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Concealer Strategy — sequential steps, not a product grid.
// ─────────────────────────────────────────────────────────────
const ConcealerStep = ({ stepNumber, match, purpose, sameAsAbove }) => {
  if (!match) return null;
  const { title, instruction } = trouveConcealerCopy(purpose);
  return (
    <li
      className="flex gap-4 sm:gap-5 items-start"
      data-testid={`concealer-step-${purpose}`}
    >
      <div className="flex-shrink-0 mt-1 h-8 w-8 rounded-full bg-[#F2E8E9] text-[#9B6A6C] text-sm font-semibold flex items-center justify-center">
        {stepNumber}
      </div>
      <div className="flex-1 rounded-2xl bg-white border border-[#EADDD7] p-4 sm:p-5">
        <div className="flex gap-4 items-start">
          <Swatch match={match} size="md" />
          <div className="min-w-0 flex-1">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#9B6A6C]">
              {title}
            </p>
            <p className="text-[15px] text-[#2C2123] font-medium mt-0.5">
              {match.brand}
            </p>
            <p className="text-sm text-[#5C4B51]">
              {match.product_name} · <span className="text-[#2C2123]">{match.shade_name}</span>
            </p>
            <div className="mt-2">
              <MatchBadge badge={match.badge} />
            </div>
            <p className="mt-3 text-sm leading-relaxed text-[#3A2C30]">
              {instruction}
            </p>
            {sameAsAbove && (
              <p
                className="mt-2 text-[11px] italic text-[#8A7D82]"
                data-testid={`concealer-step-${purpose}-same-note`}
              >
                Best available match from current catalog.
              </p>
            )}
          </div>
        </div>
      </div>
    </li>
  );
};

const ConcealerStrategy = ({ matches, preferences }) => {
  const beardMatch = matches.concealer_beard_shadow?.[0];
  const rednessMatch = matches.concealer_redness?.[0];
  const spotMatch = matches.concealer_spot?.[0];
  const brightenMatch = matches.concealer_brighten?.[0];

  // Correction step is contextual — only if any of the correction
  // signals are set on the user profile.
  const correctionPurpose = preferences?.beard_shadow
    ? "beard_shadow"
    : preferences?.redness
      ? "redness"
      : preferences?.hyperpigmentation
        ? "hyperpigmentation"
        : null;
  const correctionMatch =
    correctionPurpose === "beard_shadow" ? beardMatch :
    correctionPurpose === "redness"      ? rednessMatch :
    correctionPurpose === "hyperpigmentation" ? spotMatch :
    null;

  // Build final ordered steps, tagging duplicates so the UI can show
  // the "best available match" note instead of looking like a bug.
  const seenSignatures = new Set();
  const sig = (m) => (m ? `${m.brand}||${m.shade_name}||${m.product_name}` : null);

  const buildStep = (purpose, match) => {
    if (!match) return null;
    const s = sig(match);
    const dupe = seenSignatures.has(s);
    seenSignatures.add(s);
    return { purpose, match, dupe };
  };

  const steps = [];
  if (correctionMatch) steps.push(buildStep(correctionPurpose, correctionMatch));
  if (spotMatch)       steps.push(buildStep("spot_conceal", spotMatch));
  if (brightenMatch)   steps.push(buildStep("brighten_under_eye", brightenMatch));

  const cleaned = steps.filter(Boolean);
  if (cleaned.length === 0) return null;

  return (
    <section
      className="rounded-3xl bg-[#FBF6F1] border border-[#EADDD7] p-6 sm:p-8"
      data-testid="concealer-strategy-section"
    >
      <div className="mb-5">
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#9B6A6C]">
          Concealer
        </p>
        <h3 className="font-serif italic text-xl sm:text-2xl text-[#2C2123] mt-1">
          Next, I’d build coverage here.
        </h3>
      </div>
      <ol className="space-y-4">
        {cleaned.map((s, i) => (
          <ConcealerStep
            key={s.purpose}
            stepNumber={i + 1}
            purpose={s.purpose}
            match={s.match}
            sameAsAbove={s.dupe}
          />
        ))}
      </ol>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Lip Colors — Everyday / Nude / Statement tabs.
// ─────────────────────────────────────────────────────────────
const LipStrategy = ({ matches }) => {
  const tabs = useMemo(() => (
    [
      { key: "everyday",  label: "Everyday",  matches: matches.lip_everyday || [] },
      { key: "nude",      label: "Nude",      matches: matches.lip_nude || [] },
      { key: "statement", label: "Statement", matches: matches.lip_statement || [] },
    ].filter((t) => t.matches.length > 0)
  ), [matches]);

  const [active, setActive] = useState(tabs[0]?.key || "everyday");
  if (tabs.length === 0) return null;
  const activeTab = tabs.find((t) => t.key === active) || tabs[0];
  const topPicks = (activeTab.matches || []).slice(0, 3);

  return (
    <section
      className="rounded-3xl bg-white border border-[#EADDD7] p-6 sm:p-8"
      data-testid="lip-strategy-section"
    >
      <div className="mb-4">
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#9B6A6C]">
          Lip
        </p>
        <h3 className="font-serif italic text-xl sm:text-2xl text-[#2C2123] mt-1">
          For lips, I’d give you a few moods.
        </h3>
      </div>

      <div className="flex flex-wrap gap-2 mb-5" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={active === t.key}
            onClick={() => setActive(t.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              active === t.key
                ? "bg-[#9B6A6C] text-white"
                : "bg-[#F6EFEA] text-[#5D4050] hover:bg-[#EADDD7]"
            }`}
            data-testid={`lip-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <p className="text-sm text-[#3A2C30] mb-5 leading-relaxed">
        {trouveLipCopy(activeTab.key)}
      </p>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {topPicks.map((m) => (
          <div
            key={`${m.brand}-${m.shade_name}`}
            className="rounded-2xl bg-[#FBF6F1] border border-[#EADDD7] p-4 flex gap-4 items-start"
            data-testid={`lip-card-${activeTab.key}-${(m.shade_name || "").toLowerCase().replace(/\s+/g, "-")}`}
          >
            <Swatch match={m} size="md" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-[#2C2123] truncate">{m.brand}</p>
              <p className="text-xs text-[#5C4B51] truncate">{m.product_name}</p>
              <p className="text-xs text-[#8A7D82] mt-1">{m.shade_name}</p>
              <div className="mt-2">
                <MatchBadge badge={m.badge} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Confidence banner (top of the surface)
// ─────────────────────────────────────────────────────────────
const ConfidenceBanner = ({ confidence, onRetake }) => {
  const c = confidenceCopy(confidence);
  const bgTone = {
    high:   "bg-[#EDF4EE] border-[#D4E4D6]",
    medium: "bg-[#FBF6F1] border-[#EADDD7]",
    low:    "bg-[#FBEFEE] border-[#F0D8D6]",
  }[c.tone];

  return (
    <div
      className={`rounded-2xl border ${bgTone} p-4 sm:p-5 flex flex-wrap items-center gap-3`}
      data-testid={`confidence-banner-${c.tone}`}
    >
      <p className="text-[14px] sm:text-[15px] text-[#3A2C30] flex-1 min-w-[240px]">
        {c.line}
      </p>
      {c.action?.primary && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onRetake}
            className="inline-flex items-center gap-2 rounded-full bg-[#9B6A6C] hover:bg-[#7d5455] text-white px-4 py-2 text-sm font-medium transition-colors"
            data-testid="confidence-primary-action"
          >
            <Camera className="h-4 w-4" /> {c.action.primary}
          </button>
          {c.action.secondary && (
            <button
              type="button"
              className="inline-flex items-center rounded-full border border-[#EADDD7] text-[#5D4050] px-4 py-2 text-sm hover:bg-[#FBF6F1] transition-colors"
              data-testid="confidence-secondary-action"
            >
              {c.action.secondary}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Final CTA row
// ─────────────────────────────────────────────────────────────
const FinalCtaArea = ({ onSave, onRetake, onTryAnother }) => (
  <div
    className="rounded-3xl bg-gradient-to-br from-[#FBF6F1] to-[#F2E8E9] border border-[#EADDD7] p-6 sm:p-8 text-center"
    data-testid="routine-final-cta"
  >
    <h3 className="font-serif italic text-2xl sm:text-3xl text-[#2C2123]">
      Your routine is ready.
    </h3>
    <p className="text-sm text-[#5C4B51] mt-1">
      Save it to come back to, or tune the look.
    </p>
    <div className="mt-5 flex flex-wrap justify-center gap-3">
      {onSave && (
        <button
          type="button"
          onClick={onSave}
          className="inline-flex items-center gap-2 rounded-full bg-[#9B6A6C] hover:bg-[#7d5455] text-white px-5 py-2.5 text-sm font-medium transition-colors"
          data-testid="final-save-routine-btn"
        >
          <Bookmark className="h-4 w-4" /> Save my routine
        </button>
      )}
      {onRetake && (
        <button
          type="button"
          onClick={onRetake}
          className="inline-flex items-center gap-2 rounded-full border border-[#EADDD7] text-[#5D4050] px-5 py-2.5 text-sm hover:bg-white transition-colors"
          data-testid="final-retake-btn"
        >
          <Camera className="h-4 w-4" /> Retake selfie
        </button>
      )}
      {onTryAnother && (
        <button
          type="button"
          onClick={onTryAnother}
          className="inline-flex items-center gap-2 rounded-full border border-[#EADDD7] text-[#5D4050] px-5 py-2.5 text-sm hover:bg-white transition-colors"
          data-testid="final-try-another-btn"
        >
          <RotateCcw className="h-4 w-4" /> Try another look
        </button>
      )}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// Debug details (collapsed by default — hidden from casual demo)
// ─────────────────────────────────────────────────────────────
const DebugDetails = ({ data }) => {
  const [open, setOpen] = useState(false);
  return (
    <details
      className="rounded-2xl border border-dashed border-[#EADDD7] bg-white/40 p-3"
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
      data-testid="debug-details"
    >
      <summary className="cursor-pointer text-xs text-[#8A7D82] select-none">
        Debug details
      </summary>
      <div className="mt-3 space-y-2 text-[11px] text-[#5C4B51] font-mono">
        <div>Confidence: {Math.round((data.confidence || 0) * 100)}%</div>
        {data.detected_profile && (
          <div>
            LAB: L {data.detected_profile.lab?.L} · a {data.detected_profile.lab?.a} · b {data.detected_profile.lab?.b}
            · undertone {data.detected_profile.undertone}
            · depth {data.detected_profile.depth_score}
          </div>
        )}
        <div className="mt-2 grid gap-1">
          {Object.entries(data.matches || {}).map(([bucket, arr]) => (
            <div key={bucket}>
              <span className="text-[#9B6A6C]">{bucket}</span>:{" "}
              {(arr || []).slice(0, 3).map((m) => `${m.brand}·${m.shade_name}(${m.score})`).join(", ") || "—"}
            </div>
          ))}
        </div>
      </div>
    </details>
  );
};

// ─────────────────────────────────────────────────────────────
// Root
// ─────────────────────────────────────────────────────────────
const PersonalizedRecommendations = ({
  skinSignals,
  preferences,
  onSaveRoutine,
  onRetake,
  onTryAnother,
  onSaveProduct,
}) => {
  const { data, loading, error } = useShadeMatch({
    skinSignals,
    preferences,
    enabled: !!skinSignals?.mst_bucket,
  });

  if (!skinSignals?.mst_bucket) return null;
  if (loading) {
    return (
      <div
        className="rounded-3xl border border-[#EADDD7] bg-white/60 p-8 text-center text-[#8A7D82]"
        data-testid="personalized-recs-loading"
      >
        <Sparkles className="h-5 w-5 text-[#9B6A6C] mx-auto mb-2 animate-pulse" />
        Curating your routine…
      </div>
    );
  }
  if (error) return null;
  if (!data || data.status === "needs_new_selfie") return null;

  const matches = data.matches || {};
  const topFoundation = matches.foundation?.[0];

  const anyMatchesToShow =
    topFoundation ||
    (matches.concealer_spot?.length || matches.concealer_brighten?.length) ||
    (matches.lip_everyday?.length || matches.lip_nude?.length || matches.lip_statement?.length);

  if (!anyMatchesToShow) return null;

  return (
    <div
      className="space-y-6 sm:space-y-8"
      data-testid="personalized-recommendations"
    >
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto px-2">
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#9B6A6C]">
          Personalized picks
        </p>
        <h2 className="font-serif italic text-2xl sm:text-3xl lg:text-4xl text-[#2C2123] leading-tight mt-2">
          Your TruBeauté Routine
        </h2>
        <p className="text-sm sm:text-[15px] text-[#5C4B51] mt-2 leading-relaxed">
          I found products tailored to your skin tone, undertone, and beauty goals.
        </p>
      </div>

      {/* Confidence banner */}
      <ConfidenceBanner
        confidence={data.confidence}
        onRetake={onRetake}
      />

      {/* Foundation Hero */}
      <FoundationHero match={topFoundation} onSave={onSaveProduct} />

      {/* Concealer Strategy — hidden if no concealers ranked */}
      <ConcealerStrategy matches={matches} preferences={preferences || {}} />

      {/* Lip tabs — hidden if no lip data */}
      <LipStrategy matches={matches} />

      {/* Final CTA */}
      <FinalCtaArea
        onSave={onSaveRoutine}
        onRetake={onRetake}
        onTryAnother={onTryAnother}
      />

      {/* Debug details — collapsed */}
      <DebugDetails data={data} />
    </div>
  );
};

export default PersonalizedRecommendations;
